import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CheckedState } from '@radix-ui/react-checkbox';
import { LucideIcon } from 'lucide-react';
import { ComponentProps, FC } from 'react';

export type IconMap<T extends string> = Record<T, LucideIcon | null>;

export const SelectorIcon = <T extends string>({
  type,
  iconMap,
  className,
  iconClassName,
  ...rest
}: { type: T; iconMap: IconMap<T>; iconClassName?: string } & ComponentProps<'i'>) => {
  const Icon = iconMap[type];

  if (!Icon) return null;

  const IconComponent = Icon satisfies LucideIcon;

  return (
    <i
      className={cn('rounded-full border border-veni-green-300 bg-veni-green-50 p-1', className)}
      {...rest}
    >
      <IconComponent className={cn('size-4 text-veni-green-700', iconClassName)} />
    </i>
  );
};

export const SelectorItem = <T extends string>({
  type,
  children,
  className,
  iconMap,
  iconClassName,
  iconWrapperClassName,
  ...rest
}: ComponentProps<'div'> & {
  type: T;
  iconMap: IconMap<T>;
  iconClassName?: string;
  iconWrapperClassName?: string;
}) => {
  return (
    <div
      className={cn(
        'flex flex-nowrap items-center gap-x-1 text-sm whitespace-nowrap text-veni-gray-800',
        className,
      )}
      {...rest}
    >
      <SelectorIcon
        type={type}
        iconMap={iconMap}
        className={iconWrapperClassName}
        iconClassName={iconClassName}
      />
      {children}
    </div>
  );
};

export const SelectorCheckable: FC<
  ComponentProps<typeof Checkbox> & {
    checked: CheckedState;
    checkboxClassName?: string;
    divProps?: ComponentProps<'div'>;
  }
> = ({ children, checked, className, checkboxClassName, divProps, ...rest }) => {
  return (
    <div
      className={cn(
        'flex items-center gap-x-2 pl-4',
        checked && 'font-bold text-veni-green-900',
        className,
      )}
      {...divProps}
    >
      <Checkbox
        {...rest}
        className={cn('cursor-pointer', checkboxClassName)}
        checked={checked ?? false}
      />
      {children}
    </div>
  );
};
